import { Layers, LayoutGrid, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { ApartmentAverageForProject } from '@obraplan/core/application/use-cases/takeoff/rate-average-use-cases';
import type { StructuralPieceId } from '@obraplan/core/domain/shared/ids';
import {
  nomeDaPeca,
  pecasImportadas,
  type QuantidadesPorPeca,
} from '@obraplan/core/domain/takeoff/planilha-por-peca';
import { polygonArea } from '@obraplan/core/domain/takeoff/polygon';
import type { ServiceCatalog } from '@obraplan/core/domain/takeoff/service-catalog';
import {
  SLAB_KIND_LABEL,
  slabArea,
  slabVolume,
  type Slab,
  type SlabKind,
} from '@obraplan/core/domain/takeoff/slab';
import { Button } from '@/components/ui/button';
import { Field, Input } from '@/components/ui/input';
import { NOME_DA_PECA_MAX, NomeInput, NumeroInput } from './campos';
import { fatoresDaPecaDaFundacao, mediaDoAcoDaPeca, taxaDeBaseDaPeca } from './fatores-proprios';
import {
  AcoDaPecaDaFundacao,
  FatoresDaPecaDaFundacao,
  Linha,
  NumeroAoTerminar,
} from './FundacaoNaPrancha';
import { centroDoContorno } from './fundacao-da-prancha';
import { metros } from './formato';
import {
  corDaLaje,
  emCentimetros,
  espessuraEmTexto,
  rotuloDaLaje,
  rotuloDaRegiao,
} from './laje-da-prancha';
import { metresToPixel, type Pixel } from './plan-tracer';
import type { WorkspaceAction } from './typology-draft';
import type {
  ArrastoDaLaje,
  FerramentaDaLaje,
  FormatoDaLaje,
  LajeNaPrancha,
  PecaDaLaje,
} from './use-laje-na-prancha';

/**
 * O radier e a laje sobre a prancha, e os painéis deles (Fase 27, etapa 3 — D7, RN-369).
 *
 * A peça é o contorno preenchido, com o nome e a espessura no rótulo. A região é o contorno
 * **hachurado** por cima dela, com a espessura no rótulo: dentro dela vale a espessura da
 * região, e fora, a da peça. As cores não seguem o tema, pelo mesmo motivo das do resto do
 * traçado: a prancha é papel branco.
 */

// ── O desenho ───────────────────────────────────────────────────────────────────

const pontosDe = (vertices: readonly Pixel[]): string =>
  vertices.map((vertice) => `${String(vertice.x)},${String(vertice.y)}`).join(' ');

const idDaHachura = (kind: SlabKind): string => `laje-hachura-${kind.toLowerCase()}`;

/** O traço da hachura: a mesma largura na tela, qualquer que seja o zoom. */
function Hachuras({ zoom }: { zoom: number }) {
  const passo = 8 / zoom;
  return (
    <defs>
      {(['RADIER', 'LAJE'] as const).map((kind) => (
        <pattern
          key={kind}
          id={idDaHachura(kind)}
          patternUnits="userSpaceOnUse"
          width={passo}
          height={passo}
          patternTransform="rotate(45)"
        >
          <line x1={0} y1={0} x2={0} y2={passo} stroke={corDaLaje(kind)} strokeWidth={1.5 / zoom} />
        </pattern>
      ))}
    </defs>
  );
}

function Rotulo({
  ponto,
  texto,
  zoom,
  cor,
  daRegiao = false,
}: {
  ponto: Pixel;
  texto: string;
  zoom: number;
  cor: string;
  daRegiao?: boolean;
}) {
  const fonte = 12 / zoom;
  return (
    <text
      data-rotulo={daRegiao ? undefined : ''}
      data-rotulo-regiao={daRegiao ? '' : undefined}
      x={ponto.x}
      y={ponto.y}
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={fonte}
      fontWeight={600}
      fill={cor}
      stroke="#ffffff"
      strokeWidth={fonte * 0.22}
      paintOrder="stroke"
      className="pointer-events-none select-none"
    >
      {texto}
    </text>
  );
}

export function LajesDesenhadas({
  pecas,
  escala,
  zoom,
  selecionada,
  arrastando,
  clicaveis,
  onClicarLaje,
  onClicarRegiao,
}: {
  pecas: readonly Slab[];
  escala: number;
  zoom: number;
  selecionada: PecaDaLaje | undefined;
  arrastando: ArrastoDaLaje | undefined;
  /** Falso enquanto se traça outra coisa: aí o clique é do traço, e a peça deixa passar. */
  clicaveis: Readonly<Record<SlabKind, boolean>>;
  onClicarLaje: (id: StructuralPieceId) => void;
  onClicarRegiao: (id: StructuralPieceId, indice: number) => void;
}) {
  if (pecas.length === 0) return null;

  return (
    <g>
      <Hachuras zoom={zoom} />
      {pecas.map((peca) => {
        const clicavel = clicaveis[peca.kind];
        const naMao = arrastando?.id === peca.id && arrastando.moveu;
        const dx = naMao ? arrastando.atual.x - arrastando.origem.x : 0;
        const dy = naMao ? arrastando.atual.y - arrastando.origem.y : 0;
        const escolhida = selecionada?.tipo === 'LAJE' && selecionada.id === peca.id;
        const cor = corDaLaje(peca.kind);
        const doContorno = (contorno: readonly { x: number; y: number }[]): readonly Pixel[] =>
          contorno.map((ponto) => metresToPixel(ponto, escala));

        return (
          <g
            key={peca.id}
            data-laje={peca.id}
            data-laje-tipo={peca.kind}
            className={clicavel ? 'cursor-pointer' : 'pointer-events-none'}
            transform={naMao ? `translate(${String(dx)} ${String(dy)})` : undefined}
            onClick={(evento) => {
              if (!clicavel) return;
              evento.stopPropagation();
              onClicarLaje(peca.id);
            }}
          >
            <title>
              {`${SLAB_KIND_LABEL[peca.kind]} ${peca.name} — ${metros(slabArea(peca))} m² × ${espessuraEmTexto(peca.thickness)}`}
            </title>
            <polygon
              points={pontosDe(doContorno(peca.outline))}
              fill={cor}
              fillOpacity={0.14}
              stroke={cor}
              strokeWidth={(escolhida ? 4 : 1.5) / zoom}
              strokeLinejoin="round"
            />
            {peca.regions.map((regiao, indice) => {
              const regiaoEscolhida =
                selecionada?.tipo === 'REGIAO' &&
                selecionada.id === peca.id &&
                selecionada.indice === indice;
              const pontos = pontosDe(doContorno(regiao.outline));
              return (
                <g
                  key={indice}
                  data-regiao={peca.id}
                  data-indice={indice}
                  onClick={(evento) => {
                    if (!clicavel) return;
                    evento.stopPropagation();
                    onClicarRegiao(peca.id, indice);
                  }}
                >
                  <title>{`Região ${String(indice + 1)} — ${espessuraEmTexto(regiao.thickness)}`}</title>
                  <polygon points={pontos} fill={cor} fillOpacity={0.12} />
                  <polygon
                    points={pontos}
                    fill={`url(#${idDaHachura(peca.kind)})`}
                    stroke={cor}
                    strokeWidth={(regiaoEscolhida ? 3.5 : 1.2) / zoom}
                    strokeDasharray={`${5 / zoom} ${3 / zoom}`}
                    strokeLinejoin="round"
                  />
                  <Rotulo
                    ponto={metresToPixel(centroDoContorno(regiao.outline), escala)}
                    texto={rotuloDaRegiao(regiao)}
                    zoom={zoom}
                    cor={cor}
                    daRegiao
                  />
                </g>
              );
            })}
            <Rotulo
              ponto={metresToPixel(centroDoContorno(peca.outline), escala)}
              texto={rotuloDaLaje(peca)}
              zoom={zoom}
              cor={cor}
            />
          </g>
        );
      })}
    </g>
  );
}

/** O que está sendo traçado e ainda não foi cadastrado: o contorno da peça, ou da região. */
export function LajeEmTraco({ laje, zoom }: { laje: LajeNaPrancha; zoom: number }) {
  const { traco, ferramenta } = laje;
  if (ferramenta === undefined || traco === undefined) return null;

  const regiao = ferramenta === 'REGIAO';
  const cor = corDaLaje(regiao ? (laje.alvo?.kind ?? 'LAJE') : ferramenta);
  const tracejado = `${String(4 / zoom)} ${String(3 / zoom)}`;

  return (
    <g className="pointer-events-none">
      {traco.fechado && traco.vertices.length >= 3 ? (
        <polygon
          data-laje-nova=""
          points={pontosDe(traco.vertices)}
          fill={regiao ? `url(#${idDaHachura(laje.alvo?.kind ?? 'LAJE')})` : cor}
          fillOpacity={regiao ? 1 : 0.1}
          stroke={cor}
          strokeWidth={2 / zoom}
          strokeDasharray={tracejado}
        />
      ) : (
        <polyline
          data-laje-nova=""
          points={pontosDe(traco.ao === undefined ? traco.vertices : [...traco.vertices, traco.ao])}
          fill="none"
          stroke={cor}
          strokeWidth={2 / zoom}
          strokeDasharray={tracejado}
        />
      )}
      {traco.marcados.map((ponto, indice) => (
        <circle
          key={`l-${String(indice)}`}
          cx={ponto.x}
          cy={ponto.y}
          r={(indice === 0 ? 6 : 4) / zoom}
          fill={cor}
        />
      ))}
    </g>
  );
}

// ── Os botões da barra ──────────────────────────────────────────────────────────

/**
 * "Radier" e "Laje". O radier mora onde a estaca mora — Fundação e Contenção —, e a laje, na
 * área comum de qualquer pavimento: por isso o botão do radier vem à parte.
 */
export function BotoesDaLaje({
  ferramenta,
  comRadier,
  onEscolher,
}: {
  ferramenta: FerramentaDaLaje | undefined;
  comRadier: boolean;
  onEscolher: (ferramenta: 'RADIER' | 'LAJE') => void;
}) {
  return (
    <>
      {comRadier && (
        <Button
          size="sm"
          variant={ferramenta === 'RADIER' ? 'primary' : 'ghost'}
          onClick={() => onEscolher('RADIER')}
        >
          <LayoutGrid className="size-4" /> Radier
        </Button>
      )}
      <Button
        size="sm"
        variant={ferramenta === 'LAJE' ? 'primary' : 'ghost'}
        onClick={() => onEscolher('LAJE')}
      >
        <Layers className="size-4" /> Laje
      </Button>
    </>
  );
}

// ── Os painéis da ferramenta ────────────────────────────────────────────────────

const ROTULO_DO_FORMATO: Readonly<Record<FormatoDaLaje, string>> = {
  RETANGULO: 'Retângulo',
  POLIGONO: 'Polígono',
};

function FormaDoContorno({
  laje,
  rotulo,
}: {
  laje: LajeNaPrancha;
  /** "da peça" ou "da região" — para o leitor de tela dizer de quê. */
  rotulo: string;
}) {
  const { formato } = laje;
  return (
    <>
      <div role="group" aria-label={`Forma ${rotulo}`} className="flex flex-wrap gap-1">
        {(['RETANGULO', 'POLIGONO'] as const).map((item) => (
          <Button
            key={item}
            size="sm"
            variant={formato === item ? 'primary' : 'ghost'}
            aria-pressed={formato === item}
            onClick={() => laje.escolherFormato(item)}
          >
            {ROTULO_DO_FORMATO[item]}
          </Button>
        ))}
      </div>
      <p className="text-xs text-(--color-content-muted)">
        {formato === 'RETANGULO'
          ? 'Arraste de um canto ao outro — ou clique nos dois cantos.'
          : 'Clique os vértices do contorno e feche no primeiro ponto — ou em "Fechar contorno".'}
      </p>
    </>
  );
}

function SituacaoDoTraco({ laje, pronto }: { laje: LajeNaPrancha; pronto: string | undefined }) {
  const { formato, contorno, cantos } = laje;
  const aberto = formato === 'POLIGONO' && !laje.fechado;
  return (
    <>
      <span className="text-xs text-(--color-content-muted)">
        {contorno === undefined
          ? formato === 'RETANGULO'
            ? `${String(cantos)} de 2 cantos`
            : aberto && cantos > 0
              ? `${String(cantos)} ${cantos === 1 ? 'ponto' : 'pontos'} no contorno`
              : 'Nenhum ponto marcado na planta.'
          : (pronto ?? 'Contorno pronto.')}
      </span>
      {laje.impedimento !== undefined && (
        <span className="text-xs text-(--color-state-warn)">{laje.impedimento}</span>
      )}
      {laje.avisoDaEspessura !== undefined && (
        <span className="text-xs text-(--color-state-warn)">{laje.avisoDaEspessura}</span>
      )}
    </>
  );
}

function BotoesDoTraco({ laje, rotuloDoBotao }: { laje: LajeNaPrancha; rotuloDoBotao: string }) {
  const { formato, cantos } = laje;
  const aberto = formato === 'POLIGONO' && !laje.fechado;
  return (
    <div className="flex flex-wrap gap-2">
      <Button
        variant="primary"
        size="sm"
        disabled={laje.contorno === undefined || laje.impedimento !== undefined}
        onClick={laje.adicionar}
      >
        {rotuloDoBotao}
      </Button>
      {formato === 'POLIGONO' && (
        <>
          <Button
            variant="secondary"
            size="sm"
            disabled={!aberto || cantos < 3}
            onClick={laje.fecharContorno}
          >
            Fechar contorno
          </Button>
          <Button variant="ghost" size="sm" disabled={cantos === 0} onClick={laje.desfazerPonto}>
            Desfazer ponto
          </Button>
        </>
      )}
      <Button variant="ghost" size="sm" disabled={cantos === 0} onClick={laje.refazer}>
        Refazer
      </Button>
    </div>
  );
}

function Recusa({ erro }: { erro: string | undefined }) {
  if (erro === undefined) return null;
  return (
    <p role="alert" className="text-xs text-(--color-state-danger)">
      {erro}
    </p>
  );
}

/** O painel do radier e o da laje: o mesmo traço, o nome e a espessura. */
export function EmLaje({
  laje,
  erro,
  onSelecionar,
  onSair,
}: {
  laje: LajeNaPrancha;
  /** A última recusa do desenho — a peça arrastada para onde não pode, por exemplo. */
  erro: string | undefined;
  onSelecionar: (peca: PecaDaLaje) => void;
  onSair: () => void;
}) {
  const kind: SlabKind = laje.ferramenta === 'RADIER' ? 'RADIER' : 'LAJE';
  const radier = kind === 'RADIER';
  const rotulo = SLAB_KIND_LABEL[kind];
  // A lista existe porque a laje cobre o andar e a peça fica atrás do ambiente: por ela,
  // sempre se alcança a que se quer.
  const desenhadas = radier ? laje.radiers : laje.lajes;

  return (
    <section
      aria-label={radier ? 'Radiers' : 'Lajes'}
      className="flex flex-col gap-3 rounded-md border border-(--color-brand) p-3"
    >
      <h3 className="text-sm font-semibold">{rotulo}</h3>

      <FormaDoContorno laje={laje} rotulo="do contorno" />

      <div className="flex flex-wrap items-end gap-2">
        <Field label={`Nome ${radier ? 'do radier' : 'da laje'}`} htmlFor="laje-nome">
          <Input
            id="laje-nome"
            maxLength={NOME_DA_PECA_MAX}
            value={laje.nome}
            className="w-28"
            onChange={(evento) => laje.digitarNome(evento.target.value)}
            onKeyDown={(evento) => {
              if (evento.key === 'Enter') laje.adicionar();
            }}
          />
        </Field>
        <Field
          label={`Espessura ${radier ? 'do radier' : 'da laje'} (cm)`}
          htmlFor="laje-espessura"
        >
          <NumeroInput
            id="laje-espessura"
            value={laje.espessuraEmCm}
            className="w-24"
            onChange={laje.mudarEspessuraEmCm}
          />
        </Field>
      </div>

      <SituacaoDoTraco
        laje={laje}
        pronto={
          laje.contorno === undefined
            ? undefined
            : `Contorno pronto: ${metros(slabArea({ outline: laje.contorno }))} m² · concreto ${metros(
                slabVolume({
                  outline: laje.contorno,
                  thickness: laje.espessuraEmCm / 100,
                  regions: [],
                }),
                3,
              )} m³`
        }
      />

      <BotoesDoTraco laje={laje} rotuloDoBotao={`Adicionar ${radier ? 'radier' : 'laje'}`} />

      <Recusa erro={erro} />

      {desenhadas.length > 0 && (
        <ul aria-label="Peças já desenhadas" className="flex flex-wrap gap-1">
          {desenhadas.map((peca) => (
            <li key={peca.id}>
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Selecionar ${peca.name}`}
                onClick={() => onSelecionar({ tipo: 'LAJE', id: peca.id })}
              >
                {rotuloDaLaje(peca)}
              </Button>
            </li>
          ))}
        </ul>
      )}

      <div className="border-t border-(--color-border-subtle) pt-2">
        <Button variant="ghost" size="sm" onClick={onSair}>
          Sair
        </Button>
      </div>
    </section>
  );
}

/** O painel da região: o traço dentro da peça escolhida e a espessura de dentro. */
export function EmRegiao({
  laje,
  erro,
  onSair,
}: {
  laje: LajeNaPrancha;
  erro: string | undefined;
  onSair: () => void;
}) {
  const { alvo } = laje;

  return (
    <section
      aria-label="Região"
      className="flex flex-col gap-3 rounded-md border border-(--color-brand) p-3"
    >
      <h3 className="text-sm font-semibold">
        Região{alvo === undefined ? '' : ` em ${alvo.name}`}
      </h3>
      <p className="text-xs text-(--color-content-muted)">
        {alvo === undefined
          ? 'Escolha antes a laje ou o radier em que a região vai — clique nele na planta.'
          : `Desenhe dentro de ${alvo.name}. Na região vale a espessura dela; fora, ${espessuraEmTexto(alvo.thickness)}.`}
      </p>

      <FormaDoContorno laje={laje} rotulo="da região" />

      <Field label="Espessura da região (cm)" htmlFor="regiao-espessura">
        <NumeroInput
          id="regiao-espessura"
          value={laje.espessuraEmCm}
          className="w-24"
          onChange={laje.mudarEspessuraEmCm}
        />
      </Field>

      <SituacaoDoTraco
        laje={laje}
        pronto={
          laje.contorno === undefined
            ? undefined
            : `Região pronta: ${metros(polygonArea(laje.contorno))} m² · ${espessuraEmTexto(
                laje.espessuraEmCm / 100,
              )}`
        }
      />

      <BotoesDoTraco laje={laje} rotuloDoBotao="Adicionar região" />

      <Recusa erro={erro} />

      <div className="border-t border-(--color-border-subtle) pt-2">
        <Button variant="ghost" size="sm" onClick={onSair}>
          Sair
        </Button>
      </div>
    </section>
  );
}

// ── A peça selecionada ──────────────────────────────────────────────────────────

/**
 * Editar o radier ou a laje clicados: o nome e a espessura, as contas dele à vista, as
 * regiões, e excluir. "Região" liga o traço da região dentro desta peça.
 */
export function LajeSelecionada({
  peca,
  erro,
  dispatch: despachar,
  desenhandoRegiao,
  onDesenharRegiao,
  onSelecionarRegiao,
  onFechar,
  catalogo,
  pecasDaPlanilha,
  media,
}: {
  peca: Slab;
  /** A última recusa do desenho — só aparece depois de a pessoa editar **esta** peça. */
  erro: string | undefined;
  dispatch: React.Dispatch<WorkspaceAction>;
  desenhandoRegiao: boolean;
  onDesenharRegiao: () => void;
  onSelecionarRegiao: (indice: number) => void;
  onFechar: () => void;
  /**
   * O cadastro da obra, para os fatores próprios do radier (Fase 41, RN-538). Ausente, sem a
   * lista. A laje do pavimento não tem: o fator dela é o do apartamento (Fase 40).
   */
  catalogo?: Pick<ServiceCatalog, 'disciplines'> | undefined;
  /** A planilha por peça: o radier de nome importado não leva fator (RN-540). */
  pecasDaPlanilha?: QuantidadesPorPeca | undefined;
  /**
   * A média das obras marcadas: a do radier aparece ao lado do fator e de cada bitola, só para
   * ler (Fase 41, RN-554). Ausente, o card é o de antes.
   */
  media?: ApartmentAverageForProject | undefined;
}) {
  const [editou, setEditou] = useState(false);
  const dispatch: React.Dispatch<WorkspaceAction> = (acao) => {
    setEditou(true);
    despachar(acao);
  };
  const rotulo = SLAB_KIND_LABEL[peca.kind];
  const fatores = useMemo(
    () => fatoresDaPecaDaFundacao(catalogo, 'RADIER', peca, media),
    [catalogo, peca, media],
  );
  const mediaDoAco = useMemo(() => mediaDoAcoDaPeca(media, 'RADIER'), [media]);

  return (
    <section
      aria-label={`${rotulo} ${peca.name}`}
      className="flex flex-col gap-2 rounded-md border border-(--color-brand) p-3"
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">
          {rotulo} {peca.name}
        </h3>
        <Button variant="ghost" size="sm" onClick={onFechar}>
          Fechar
        </Button>
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <Field label="Nome" htmlFor={`laje-${peca.id}-nome`}>
          <NomeInput
            id={`laje-${peca.id}-nome`}
            value={peca.name}
            className="w-28"
            respostaImediata
            onDescartar={() => dispatch({ type: 'DISMISS_ERROR' })}
            onCommit={(nome) => dispatch({ type: 'UPDATE_SLAB', pieceId: peca.id, name: nome })}
          />
        </Field>
        <Field label="Espessura (cm)" htmlFor={`laje-${peca.id}-espessura`}>
          <NumeroAoTerminar
            id={`laje-${peca.id}-espessura`}
            valor={emCentimetros(peca.thickness)}
            className="w-24"
            onCommit={(espessura) =>
              dispatch({ type: 'UPDATE_SLAB', pieceId: peca.id, thickness: espessura / 100 })
            }
          />
        </Field>
      </div>

      <div className="flex flex-col gap-1 text-xs">
        <Linha rotulo="Área em planta" valor={`${metros(slabArea(peca))} m²`} />
        <Linha rotulo="Concreto" valor={`${metros(slabVolume(peca), 3)} m³`} />
        <Linha rotulo="Regiões" valor={String(peca.regions.length)} />
      </div>

      {peca.regions.length > 0 && (
        <ul aria-label="Regiões da peça" className="flex flex-wrap gap-1">
          {peca.regions.map((regiao, indice) => (
            <li key={indice}>
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Selecionar a região ${String(indice + 1)}`}
                onClick={() => onSelecionarRegiao(indice)}
              >
                Região {indice + 1} · {rotuloDaRegiao(regiao)}
              </Button>
            </li>
          ))}
        </ul>
      )}

      {peca.kind === 'RADIER' && catalogo !== undefined && (
        <FatoresDaPecaDaFundacao
          peca={peca}
          rotulo="radier"
          servicos={fatores}
          daPlanilha={pecasImportadas(pecasDaPlanilha).has(nomeDaPeca(peca.name))}
          semFator="não leva fator próprio"
          rotuloDaVolta="Voltar ao índice"
          dispatch={dispatch}
        />
      )}
      {/* A taxa de aço própria do radier (Fase 41, etapa 2 — RN-544). */}
      {peca.kind === 'RADIER' && catalogo !== undefined && (
        <AcoDaPecaDaFundacao
          peca={peca}
          rotulo="radier"
          base={taxaDeBaseDaPeca(catalogo, 'RADIER')}
          daPlanilha={pecasImportadas(pecasDaPlanilha).has(nomeDaPeca(peca.name))}
          dispatch={dispatch}
          media={mediaDoAco}
        />
      )}

      {editou && erro !== undefined && <Recusa erro={erro} />}

      <div className="flex flex-wrap gap-2">
        <Button
          variant={desenhandoRegiao ? 'primary' : 'secondary'}
          size="sm"
          aria-pressed={desenhandoRegiao}
          onClick={onDesenharRegiao}
        >
          Região
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            dispatch({ type: 'REMOVE_SLAB', pieceId: peca.id });
            onFechar();
          }}
        >
          <Trash2 className="size-4" /> Excluir {peca.kind === 'RADIER' ? 'radier' : 'laje'}
        </Button>
      </div>
    </section>
  );
}

/** Editar a região clicada: a espessura, a área e o concreto dela, e excluir. */
export function RegiaoSelecionada({
  peca,
  indice,
  erro,
  dispatch: despachar,
  onFechar,
}: {
  peca: Slab;
  indice: number;
  erro: string | undefined;
  dispatch: React.Dispatch<WorkspaceAction>;
  onFechar: () => void;
}) {
  const [editou, setEditou] = useState(false);
  const regiao = peca.regions[indice];
  if (regiao === undefined) return null;

  const dispatch: React.Dispatch<WorkspaceAction> = (acao) => {
    setEditou(true);
    despachar(acao);
  };
  const area = polygonArea(regiao.outline);

  return (
    <section
      aria-label={`Região ${String(indice + 1)} de ${peca.name}`}
      className="flex flex-col gap-2 rounded-md border border-(--color-brand) p-3"
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">
          Região {indice + 1} de {peca.name}
        </h3>
        <Button variant="ghost" size="sm" onClick={onFechar}>
          Fechar
        </Button>
      </div>

      <Field label="Espessura da região (cm)" htmlFor={`laje-${peca.id}-regiao-${String(indice)}`}>
        <NumeroAoTerminar
          id={`laje-${peca.id}-regiao-${String(indice)}`}
          valor={emCentimetros(regiao.thickness)}
          className="w-24"
          onCommit={(espessura) =>
            dispatch({
              type: 'UPDATE_SLAB_REGION',
              pieceId: peca.id,
              index: indice,
              thickness: espessura / 100,
            })
          }
        />
      </Field>

      <div className="flex flex-col gap-1 text-xs">
        <Linha rotulo="Área da região" valor={`${metros(area)} m²`} />
        <Linha rotulo="Concreto da região" valor={`${metros(area * regiao.thickness, 3)} m³`} />
        <Linha rotulo={`Fora dela, em ${peca.name}`} valor={espessuraEmTexto(peca.thickness)} />
      </div>

      {editou && erro !== undefined && <Recusa erro={erro} />}

      <div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            dispatch({ type: 'REMOVE_SLAB_REGION', pieceId: peca.id, index: indice });
            onFechar();
          }}
        >
          <Trash2 className="size-4" /> Excluir região
        </Button>
      </div>
    </section>
  );
}
